import { useAtom } from "jotai";
import { Redo2, Undo2 } from "lucide-react";

import { trackImageEditToolRedo, trackImageEditToolUndo } from "@design-lab/shared/lib/analytics";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { useRefineHistoryContext, interactionModeAtom } from "@design-lab/entities/artwork";
import { BackgroundToggle } from "@design-lab/widgets/artboard/ui/tool-bar/BackgroundToggle";

import type { BackgroundType } from "@design-lab/shared/constants";

import { RefineModeSelector } from "./RefineModeSelector";
import { RefineToolTabs } from "./RefineToolTabs";
import { RefineZoom } from "./RefineZoom";

type RefineDesktopControlsProps = {
  backgroundType: BackgroundType;
  onBackgroundTypeChange: (type: BackgroundType) => void;
  activeTool: string;
  onToolChange: (tool: string) => void;
  artworkId?: string;
};

export function RefineDesktopControls({
  backgroundType,
  onBackgroundTypeChange,
  activeTool,
  onToolChange,
  artworkId
}: RefineDesktopControlsProps) {
  const { canUndo, canRedo, undo, redo } = useRefineHistoryContext();
  const [interactionMode, setInteractionMode] = useAtom(interactionModeAtom);

  const handleUndo = () => {
    if (artworkId) {
      trackImageEditToolUndo(artworkId);
    }
    undo();
  };

  const handleRedo = () => {
    if (artworkId) {
      trackImageEditToolRedo(artworkId);
    }
    redo();
  };

  return (
    <>
      <div className="pointer-events-auto absolute left-2 top-2 z-10 flex gap-2">
        <BackgroundToggle
          value={backgroundType}
          onValueChange={onBackgroundTypeChange}
          forceFullWidth
        />
        <RefineModeSelector value={interactionMode} onValueChange={setInteractionMode} />
      </div>

      <div className="pointer-events-auto absolute left-2/3 top-2 z-10 flex gap-2 xl:left-1/2 xl:-translate-x-1/2">
        <Button
          onPress={handleUndo}
          isDisabled={!canUndo}
          className="box-border flex aspect-square items-center justify-center rounded-lg border border-blue-700 bg-white px-3 py-2.5 text-base leading-6 text-blue-700 transition-all hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-gray-100 disabled:text-gray-600 disabled:opacity-70"
          data-testid="refine-undo"
          aria-label="Undo (Cmd/Ctrl + Z)"
          size="xl"
          variant="icon"
        >
          <Undo2 className="size-5" />
        </Button>
        <Button
          onPress={handleRedo}
          isDisabled={!canRedo}
          className="box-border flex aspect-square items-center justify-center rounded-lg border border-blue-700 bg-white px-3 py-2.5 text-base leading-6 text-blue-700 transition-all hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-gray-100 disabled:text-gray-600 disabled:opacity-70"
          data-testid="refine-redo"
          aria-label="Redo (Cmd/Ctrl + Shift + Z)"
          size="xl"
          variant="icon"
        >
          <Redo2 className="size-5" />
        </Button>
      </div>

      <div className="pointer-events-auto absolute right-2 top-2 z-10">
        <RefineZoom maxZoom={10} />
      </div>

      <RefineToolTabs activeTool={activeTool} onToolChange={onToolChange} />
    </>
  );
}
